import { useEffect, useState } from 'react';
import {
  ExportCsv,
  Toolbar,
  useGridApiContext,
  GridColumnVisibilityModel,
  ColumnsPanelTrigger
} from '@mui/x-data-grid-pro';
import Tooltip from '@mui/material/Tooltip';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import FormatLineSpacingIcon from '@mui/icons-material/FormatLineSpacing';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
// Order avoids a PageCard/BasicDataGrid <-> AssetGridColumnDefs circular-import bug.
import { PageCard } from '../../../../Compounds/CardWithTitle';
import { BasicDataGrid } from '../../../../Compounds/Table/BasicDataGrid';
import { DEFAULT_TABLE_STYLES } from '../../../../../utils/styleHelpers';
import { ComponentSpinner } from '../../../../Compounds/Loading/ComponentSpinner';
import { DefaultCustomerMergeListGridColumnDefs } from '../../ColumnDefs/CustomerMergeListGridColumnDefs';
import { mergeCustomerList } from '../../../../../Api/CustomerMap/CustomerMapApi';
import { CustomQuickFilter } from '../../../../Compounds/UpstackDataGrid/CustomQuickFilter';
import { fileNameHelper } from '../../../../../utils/fileNameHelper';

export interface MergeRpmCustomerRecord {
  mergeCustomerId: number;
  mergeCustomerName: string;
  masterCustomerId: number;
  masterCustomerName: string;
  sfdcCustomerId: string;
  sfdcCustomerName: string;
}

const CustomToolbar = () => {
  const apiRef = useGridApiContext();

  const [densityMenuAnchor, setDensityMenuAnchor] = useState<null | HTMLElement>(null);
  const handleDensityChange = (density: 'compact' | 'standard' | 'comfortable') => {
    apiRef.current.setDensity(density);
    setDensityMenuAnchor(null);
  };

  return (
    <Toolbar>
      <Box
        sx={{
          padding: '1rem 0rem 1rem 1rem',
          gap: 1,
          flexWrap: 'wrap',
          display: 'flex',
          alignItems: 'left',
          width: '100%'
        }}>
        <CustomQuickFilter />
        <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 0, flexWrap: 'wrap' }}>
          <Tooltip
            title="Select columns"
            enterDelay={200}>
            <ColumnsPanelTrigger
              render={
                <Button
                  startIcon={<ViewColumnIcon fontSize="small" />}
                  sx={{ fontSize: '.8rem', color: 'secondary.main' }}>
                  Columns
                </Button>
              }
            />
          </Tooltip>

          <Tooltip
            title="Density"
            enterDelay={200}>
            <Button
              startIcon={<FormatLineSpacingIcon fontSize="small" />}
              onClick={(e) => setDensityMenuAnchor(e.currentTarget)}
              sx={{ fontSize: '.8rem', color: 'secondary.main' }}>
              Density
            </Button>
          </Tooltip>

          <Menu
            anchorEl={densityMenuAnchor}
            open={Boolean(densityMenuAnchor)}
            onClose={() => setDensityMenuAnchor(null)}>
            <MenuItem onClick={() => handleDensityChange('compact')}>
              <FormatAlignJustifyIcon
                fontSize="small"
                sx={{ marginRight: 1 }}
              />
              Compact
            </MenuItem>
            <MenuItem onClick={() => handleDensityChange('standard')}>
              <DensityMediumIcon
                fontSize="small"
                sx={{ marginRight: 1 }}
              />
              Standard
            </MenuItem>
            <MenuItem onClick={() => handleDensityChange('comfortable')}>
              <DensitySmallIcon
                fontSize="small"
                sx={{ marginRight: 1 }}
              />
              Comfortable
            </MenuItem>
          </Menu>

          <Tooltip
            title="Download as CSV"
            enterDelay={200}>
            <ExportCsv
              options={{ fileName: fileNameHelper('RPM Customer Merge List') }}
              render={
                <Button
                  startIcon={<FileDownloadIcon fontSize="small" />}
                  sx={{ fontSize: '.8rem', color: 'secondary.main' }}>
                  Export
                </Button>
              }
            />
          </Tooltip>
        </Box>
      </Box>
    </Toolbar>
  );
};

export const CustomerMergeList = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<MergeRpmCustomerRecord[]>([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    mergeCustomerId: false,
    masterCustomerId: false,
    sfdcCustomerId: false
  });

  const loadData = async () => {
    setIsLoading(true);
    const { error, data } = await mergeCustomerList();
    if (error) {
      setIsLoading(false);
      return;
    }
    if (data) {
      setRows(data);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const sxStyle = {
    ...DEFAULT_TABLE_STYLES.sx,
    maxHeight: 'calc(100vh - 250px)',
    '.MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': {
      marginLeft: 'auto'
    },
    borderWidth: '0',
    borderTopLeftRadius: '0',
    borderTopRightRadius: '0',
    borderBottomLeftRadius: '.75rem',
    borderBottomRightRadius: '.75rem'
  };

  return (
    <PageCard
      dataTestId="merge-customer-list"
      dataCy="merge-customer-list"
      childrenWrapperClass="max-w-[60vw] min-w-full"
      title="RPM Customer Merge List">
      <>
        {isLoading && (
          <div className="flex h-[10rem] m-auto">
            <ComponentSpinner label="Loading data..." />
          </div>
        )}
        {!isLoading && (
          <BasicDataGrid
            rows={rows}
            columns={DefaultCustomerMergeListGridColumnDefs}
            sxStyle={sxStyle}
            disableColumnMenu
            showColumnVerticalBorder
            showCellVerticalBorder
            slots={{ toolbar: CustomToolbar }}
            showToolbar
            slotProps={{ toolbar: { showSearch: true } }}
            primaryKey="mergeCustomerId"
            hideFooterRowCount={false}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
            initialState={{
              sorting: {
                sortModel: [
                  { field: 'masterCustomerName', sort: 'asc' },
                  { field: 'mergeCustomerName', sort: 'asc' }
                ]
              }
            }}
          />
        )}
      </>
    </PageCard>
  );
};
